import { FieldValues, Path } from "react-hook-form";
import { nameof } from "ts-simple-nameof";

import {
  AddressType,
  ShippingTypeBookingTemplates,
  TransferPlace,
  BookingTemplateConstants,
} from "@assets/constants";
import {
  CargoPackageParentType,
  Commodity,
  CommonTypes,
  RegistryTypes,
  PortTypes,
  SelectOption,
  BookingTemplateTypes,
} from "@assets/types";

export interface PartnerForm {
  PartnerFillOutType: BookingTemplateConstants.PARTNER_FILL_OUT_TYPE;
  PartnerCompany: BookingTemplateTypes.Partner | null;
  CompanyName: string;
  StreetAndNumber: string;
  City: string;
  PostalCode: string;
  County: string;
  Country: string;
  AdditionalAddressInfo: string;
  ContactName: string;
  Email: string;
  Phone: string;
  CityPort: PortTypes.Port | null;
}

export const defaultPartnerForm: PartnerForm = {
  PartnerFillOutType: BookingTemplateConstants.PARTNER_FILL_OUT_TYPE.PREVIOUS,
  PartnerCompany: null,
  CompanyName: "",
  StreetAndNumber: "",
  City: "",
  PostalCode: "",
  CityPort: null,
  County: "",
  Country: "",
  AdditionalAddressInfo: "",
  ContactName: "",
  Email: "",
  Phone: "",
};

export interface TransportRequestForm {
  Id: CommonTypes.UUID;
  CompanyName: string;
  StreetAndNumber: string;
  City: string;
  PostalCode: string;
  County: string;
  Country: string;
  AdditionalAddressInfo: string;
  ContactName: string;
  Email: string;
  Phone: string;
}

export const defaultTransportRequestForm: TransportRequestForm = {
  Id: `a-b-c-d-e`,
  CompanyName: "",
  StreetAndNumber: "",
  City: "",
  PostalCode: "",
  County: "",
  Country: "",
  AdditionalAddressInfo: "",
  ContactName: "",
  Email: "",
  Phone: "",
};

export interface BookingTemplateForm extends RouteForm, CargoForm {}

export interface RouteForm extends FieldValues {
  BookingTemplateName: string;
  BookingParty: string;
  BookingPartyType: AddressType;
  Shipper: PartnerForm | null;
  Consignee: PartnerForm | null;
  Notifier: PartnerForm | null;
  FromTransferPlace: TransferPlace;
  FromCountry: CommonTypes.Country | null;
  PLR: PortTypes.Port | null; // place of receipt
  PFD: PortTypes.Port | null; // place of delivery
  FromStops: TransportRequestForm[];
  ToTransferPlace: TransferPlace;
  ToCountry: CommonTypes.Country | null;
  POL: PortTypes.Port | null;
  POD: PortTypes.Port | null;
  ToStops: TransportRequestForm[];
  Incoterm: RegistryTypes.Incoterm | null;
  IncotermPoint: PortTypes.Port | null;
}

export const defaultRouteForm: RouteForm = {
  BookingTemplateName: "",
  BookingParty: "",
  BookingPartyType: AddressType.CEE,
  Shipper: null,
  Consignee: null,
  Notifier: null,
  FromTransferPlace: TransferPlace.ADDRESS,
  FromCountry: null,
  PLR: null,
  PFD: null,
  FromStops: [],
  ToTransferPlace: TransferPlace.ADDRESS,
  ToCountry: null,
  POL: null,
  POD: null,
  ToStops: [],
  Incoterm: null,
  IncotermPoint: null,
};

export interface DetailForm {
  ShipmentPackaging: CargoPackageParentType | null;
  Commodity: Commodity | null;
  CargoDescription: string;
  Hazardous: boolean;
  OversizeFlag: boolean;
  Refrigerated: boolean;
  Temperature: SelectOption<number> | null;
  VentilationVolume: number | null;
}

export const defaultDetailForm: DetailForm = {
  ShipmentPackaging: null,
  Commodity: null,
  CargoDescription: "",
  Hazardous: false,
  OversizeFlag: false,
  Refrigerated: false,
  Temperature: null,
  VentilationVolume: null,
};

export interface CargoForm {
  ShippingType: ShippingTypeBookingTemplates;
  Details: DetailForm[];
  HSCodes: CommonTypes.HSCode[];
  BillOfLadingType: SelectOption<string> | null;
  CountryOfOrigin: CommonTypes.Country | null;
  Comment: string;
}

export const defaultCargoForm: CargoForm = {
  ShippingType: ShippingTypeBookingTemplates.FF,
  Details: [defaultDetailForm],
  HSCodes: [],
  BillOfLadingType: null,
  CountryOfOrigin: null,
  Comment: "",
};

export interface TransferAddressSectionFields {
  Country: Path<RouteForm>;
  TransferType: Path<RouteForm>;
  Point: Path<RouteForm>;
  Port: Path<RouteForm>;
  TransportRequest: Path<RouteForm>;
}

export const transferAddressFormFieldNames: {
  From: TransferAddressSectionFields;
  To: TransferAddressSectionFields;
} = {
  From: {
    Country: nameof<RouteForm>((item) => item.FromCountry),
    TransferType: nameof<RouteForm>((item) => item.FromTransferPlace),
    Point: nameof<RouteForm>((item) => item.PLR),
    Port: nameof<RouteForm>((item) => item.POL),
    TransportRequest: nameof<RouteForm>((item) => item.FromStops),
  },
  To: {
    Country: nameof<RouteForm>((item) => item.ToCountry),
    TransferType: nameof<RouteForm>((item) => item.ToTransferPlace),
    Point: nameof<RouteForm>((item) => item.PFD),
    Port: nameof<RouteForm>((item) => item.POD),
    TransportRequest: nameof<RouteForm>((item) => item.ToStops),
  },
};
